import { animate, keyframes, transition, trigger } from '@angular/animations';
import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ElementRef } from '@angular/core';
import { IProduct2 } from 'situview-webapi-client';
import * as kf from '../../../core/keyframes';
import { Utils } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'thumb-product-card',
  templateUrl: './thumb-product-card.component.html',
  styleUrls: ['./thumb-product-card.component.scss'],
  animations: [
    trigger('cardAnimator', [
      transition('* => wobble', animate(1000, keyframes(kf.wobble))),
      transition('* => swing', animate(1000, keyframes(kf.swing))),
      transition('* => jello', animate(1000, keyframes(kf.jello))),
      transition('* => zoomOutRight', animate(1000, keyframes(kf.zoomOutRight))),
      transition('* => slideOutLeft', animate(1000, keyframes(kf.slideOutLeft))),
      transition('* => rotateOutUpRight', animate(1000, keyframes(kf.rotateOutUpRight))),
      transition('* => flipOutY', animate(1000, keyframes(kf.flipOutY))),
      transition('* => headShake', animate(1000, keyframes(kf.headShake)))
    ])
  ]
})
export class ThumbProductCardComponent implements OnInit, OnChanges {
  @Input() item: IProduct2 | undefined;
  @Input() selectedCompanyId: string = '';
  @Input() isCatalog: boolean = true;
  @Input() canExpandImage: boolean = false;
  @Input() isSituUser: boolean = true;
  @Input() isAdmin: boolean = false;
  @Input() isExternalProductContractorUser: boolean = false;
  @Input() isCompanyProductPublisherUser: boolean = false;
  @Input() searchTerm: string = "";
  @Input() selected: boolean = false;
  @Input() animationState: string = '';
  @Input() situView: string = '';
  @Input() companyName: string = '';
  @Input() companyLogo: string = '';
  @Input() productTypeColor: string = '';
  @Input() productTypeName: string = '';

  @Output() filterCompanyId: EventEmitter<string> = new EventEmitter();
  @Output() filterProductTypeId: EventEmitter<string> = new EventEmitter();
  @Output() createPreview: EventEmitter<string> = new EventEmitter();
  @Output() shareSituLink: EventEmitter<string> = new EventEmitter();
  @Output() productDownloadRequest: EventEmitter<ProductDownloadRequest> = new EventEmitter();

  showDownloadOptions: boolean = false;

  isImageExpanded: boolean = false;
  isImageShrinking: boolean = false;
  imageHoverTimeout: any;

  imageName = 'thumbnail';
  imageX = 0;
  imageY = 0;
  nextImageToPreload?: string;

  expandedCardTranslationX = 0;

  constructor(private elem: ElementRef<HTMLElement>,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar
  ) {
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.animationState) {
      this.startAnimation(changes.animationState.currentValue);
    }
  }

  ngAfterViewInit() {
    // Calculate the translation based on image and container size
    this.calculateTranslation();
  }

  startAnimation(state: string) {
    if (!this.animationState) {
      this.animationState = state;
    }
  }

  resetAnimationState() {
    this.animationState = '';
  }

  selectCompanyByProduct(companyId: string) {
    this.filterCompanyId.emit(companyId);
  }

  selectProductTypeByProduct(productTypeId: string) {
    this.filterProductTypeId.emit(productTypeId);
  }

  copyManufacturerProductDetails(item: IProduct2) {
    if (item) {
      this.clipboard.copy(`Manufacturer: "${this.companyName}" / SKU: "${this.getKeySku(item)}"`);
        this.snackBar.open(`Copied to product details to clipboard.`, "Close", {
          duration: 5000,
          panelClass: ['copied-snackbar']
        });
    }
  }

  getKeySku(item: IProduct2) {
    if (item) {
      return item.callerReferenceKey?.split("\\").reverse()[0];
    }
  }

  getPreview(callerReferenceKey: string) {
    this.createPreview.emit(callerReferenceKey);
  }

  share(artifactId: string) {
    this.shareSituLink.emit(artifactId);
  }

  downloadFile(file: string) {
    if (this.item) {
      const extension = Utils.getFileExtension(file);
      this.productDownloadRequest.emit(new ProductDownloadRequest(this.item!.previewArtifactId!, file, `${this.item.name}${this.item.sku}.${extension}`))
    }
  }

  onMouseEnterImage() {
    if (!this.canExpandImage || !this.item?.previewArtifactId) {
      return;
    }

    this.imageHoverTimeout = setTimeout(() => {
      this.isImageExpanded = true; // Expand the image after 1 second

      this.expandedCardTranslationX = this.calculateTranslation();
    }, 1000); // 1000 ms = 1 second
  }

  onMouseLeaveImage() {
    if (!this.canExpandImage || !this.item?.previewArtifactId) {
      return;
    }

    clearTimeout(this.imageHoverTimeout); // Clear the timer if the mouse leaves before 1 second
    this.imageHoverTimeout = undefined;

    setTimeout(() => {
      if (!this.imageHoverTimeout) {
        this.isImageExpanded = false; // Reset the image back to normal size
        this.isImageShrinking = true; // Set the image to shrinking so the z-index can be managed
        this.expandedCardTranslationX = 0;

        setTimeout(() => {
          this.isImageShrinking = false;
        }, 200);
      }
    }, 200); // 1000 ms = 1 second

  }

  onMouseMoveImage(event: MouseEvent) {
    // Get the dimensions of the image
    const imgElement = event.target as HTMLElement;
    const rect = imgElement.getBoundingClientRect();
    const x = event.clientX - rect.left; // Mouse X relative to the image
    const y = event.clientY - rect.top;  // Mouse Y relative to the image

    // Calculate which cell the mouse is over
    const column = Math.min(Math.floor(x / (rect.width / 3)), 2); // Divide width into 3 columns (0 - 2)
    const row = Math.min(Math.floor(y / (rect.height / 3)), 2);   // Divide height into 3 rows (0 - 2)

    // Set the hover image based on the calculated cell
    this.imageName = `thumbnail_${row}_${column}`;
    
    // Calculate the image translation based on mouse position
    const translateFactor = -5; // Factor to control the amount of translation
    this.imageX = ((x / rect.width) - 0.5) * translateFactor;
    this.imageY = ((y / rect.height) - 0.5) * translateFactor;
  }

  calculateTranslation() : number {
    if (!this.canExpandImage || !this.item?.previewArtifactId) {
      return 0;
    }

    // NOTE: Add product-expanded-image-container to the parent container if you want to shift the expanded image to always be inside of the parent container
    const imageContainerElement = document.getElementsByClassName('product-expanded-image-container').item(0) as HTMLElement;

    if (!imageContainerElement) {
      return 0;
    }

    const productCardElement = this.elem.nativeElement;

    const imageRect = productCardElement.getBoundingClientRect();
    const wrapperRect = imageContainerElement.getBoundingClientRect();

    const imageCenterX = imageRect.x + (imageRect.width * 0.5);
    // Image is scaled to 3 times its original size
    const scaleFactor = 3;

    const scaledImageLeft = imageCenterX - (imageRect.width * scaleFactor * 0.5);
    if (scaledImageLeft < wrapperRect.left) {
      return (wrapperRect.left - scaledImageLeft) * 0.5;
    }

    const scaledImageRight = imageCenterX + (imageRect.width * scaleFactor * 0.5);
    if (scaledImageRight > wrapperRect.right) {
      return (wrapperRect.right - scaledImageRight) * 0.5;
    }

    return 0;
  }
}

export class ProductDownloadRequest {
  constructor(public artifactId: string, public file: string, public fileName: string) {}
}
